import { makeStyles, tokens, Text, Badge } from '@fluentui/react-components';
import type { TraceStep } from '../api/traces';

// Workbench MVP Session A — step timeline for a single trace.
// Stateless: parent passes an already-loaded step array.

interface Props {
  steps: TraceStep[];
}

const useStyles = makeStyles({
  root: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '12px 14px',
  },
  header: {
    fontWeight: 600,
    marginBottom: '10px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1.5fr 0.9fr 0.9fr 1fr auto',
    gap: '10px',
    alignItems: 'center',
    padding: '6px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  step: {
    fontWeight: 500,
  },
  provider: {
    color: tokens.colorNeutralForeground2,
  },
  latency: {
    textAlign: 'right' as const,
  },
  breach: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 600,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    padding: '18px 0',
    textAlign: 'center' as const,
  },
});

function fmtMs(v: number | null): string {
  if (v === null || v === undefined) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(2)}s`;
  return `${v.toFixed(0)}ms`;
}

function statusBadge(status: TraceStep['status']) {
  if (status === 'failed') return <Badge appearance="filled" color="danger">failed</Badge>;
  if (status === 'started') return <Badge appearance="tint" color="informative">started</Badge>;
  return <Badge appearance="tint" color="success">completed</Badge>;
}

export function TraceStepList({ steps }: Props) {
  const s = useStyles();

  if (steps.length === 0) {
    return (
      <div className={s.root}>
        <div className={s.header}>Steps</div>
        <div className={s.empty}>No telemetry captured for this trace.</div>
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div className={s.header}>Steps ({steps.length})</div>
      {steps.map((step) => (
        <div key={step.eventId} className={s.row}>
          <div className={s.step}>
            <Text>{step.step}</Text>
            <div style={{ fontSize: 11, color: tokens.colorNeutralForeground3 }}>
              node: {step.nodeId}
            </div>
          </div>
          <div className={s.provider}>{step.providerId ?? '—'}</div>
          <div>{statusBadge(step.status)}</div>
          <div className={s.latency}>
            {fmtMs(step.latencyMs)}
            {step.slaBudgetMs !== null && (
              <span style={{ color: tokens.colorNeutralForeground3, marginLeft: 6, fontSize: 11 }}>
                / {fmtMs(step.slaBudgetMs)}
              </span>
            )}
          </div>
          <div>
            {step.slaBreached ? <span className={s.breach}>breach</span> : null}
          </div>
        </div>
      ))}
    </div>
  );
}
